/**
 * Retry and Backoff Utilities
 * 
 * Provides robust retry logic with exponential backoff for API calls.
 */

export interface RetryOptions {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  retryableStatusCodes: number[];
  onRetry?: (attempt: number, error: any) => void;
}

export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 3,
  initialDelayMs: 1000, // 1 second
  maxDelayMs: 30000, // 30 seconds
  backoffMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  onRetry: undefined,
};

/**
 * Sleep for a specified number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculate exponential backoff delay with jitter
 */
function calculateBackoff(attempt: number, options: RetryOptions): number {
  const exponentialDelay = options.initialDelayMs * Math.pow(options.backoffMultiplier, attempt);
  const cappedDelay = Math.min(exponentialDelay, options.maxDelayMs);
  
  // Add jitter (random ±20%)
  const jitter = cappedDelay * 0.2 * (Math.random() * 2 - 1);
  const finalDelay = Math.max(0, cappedDelay + jitter);
  
  return Math.floor(finalDelay);
}

/**
 * Check if an error is retryable
 */
function isRetryable(error: any, options: RetryOptions): boolean {
  // Network errors are retryable
  if (error.name === 'TypeError' || error.message?.includes('fetch')) {
    return true;
  }
  
  // HTTP errors with retryable status codes
  if (error.status && options.retryableStatusCodes.includes(error.status)) {
    return true;
  }
  
  // Timeout errors
  if (error.name === 'AbortError' || error.message?.includes('timeout')) {
    return true;
  }
  
  return false;
}

/**
 * Retry a function with exponential backoff
 * 
 * @param fn - Async function to retry
 * @param options - Retry options (optional)
 * @returns Promise<T> - Result of successful function call
 * @throws Error - If all retries fail
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const opts: RetryOptions = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: any;
  
  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry if not retryable or if this was the last attempt
      if (!isRetryable(error, opts) || attempt === opts.maxRetries) {
        throw error;
      }
      
      // Calculate backoff delay
      const delay = calculateBackoff(attempt, opts);
      
      // Call onRetry callback if provided
      if (opts.onRetry) {
        opts.onRetry(attempt + 1, error);
      }
      
      // Log retry attempt
      console.warn(
        `Retry attempt ${attempt + 1}/${opts.maxRetries} after ${delay}ms`,
        { error: error.message || error }
      );
      
      // Wait before retrying
      await sleep(delay);
    }
  }
  
  // Should never reach here, but TypeScript needs it
  throw lastError;
}

/**
 * Retry a fetch request with exponential backoff
 * 
 * @param url - URL to fetch
 * @param init - Fetch init options
 * @param options - Retry options (optional)
 * @returns Promise<Response> - Fetch response
 */
export async function fetchWithRetry(
  url: string,
  init?: RequestInit,
  options: Partial<RetryOptions> = {}
): Promise<Response> {
  return withRetry(async () => {
    const response = await fetch(url, init);
    
    // Throw error for retryable HTTP status codes
    if (!response.ok && options.retryableStatusCodes?.includes(response.status)) {
      const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      error.response = response;
      throw error;
    }
    
    return response;
  }, options);
}

/**
 * Rate limiter to prevent exceeding API rate limits
 */
export class RateLimiter {
  private queue: Array<() => void> = [];
  private activeCount = 0;
  private lastCallTime = 0;

  constructor(
    private maxConcurrent: number = 5,
    private minIntervalMs: number = 1000
  ) {}

  /**
   * Execute a function with rate limiting
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Wait for available slot
    await this.waitForSlot();
    
    // Execute function
    this.activeCount++;
    try {
      return await fn();
    } finally {
      this.activeCount--;
      this.processQueue();
    }
  }

  private async waitForSlot(): Promise<void> {
    // Check if we can run immediately
    if (this.activeCount < this.maxConcurrent) {
      // Ensure minimum interval between calls
      const now = Date.now();
      const timeSinceLastCall = now - this.lastCallTime;
      if (timeSinceLastCall < this.minIntervalMs) {
        await sleep(this.minIntervalMs - timeSinceLastCall);
      }
      this.lastCallTime = Date.now();
      return;
    }
    
    // Wait for a slot to become available
    return new Promise<void>(resolve => {
      this.queue.push(resolve);
    });
  }

  private processQueue(): void {
    if (this.queue.length > 0 && this.activeCount < this.maxConcurrent) {
      const resolve = this.queue.shift();
      if (resolve) {
        this.lastCallTime = Date.now();
        resolve();
      }
    }
  }
}

/**
 * Circuit breaker to prevent cascading failures
 */
export class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';

  constructor(
    private failureThreshold: number = 5,
    private resetTimeoutMs: number = 60000, // 1 minute
    private onStateChange?: (state: 'closed' | 'open' | 'half-open') => void
  ) {}

  /**
   * Execute a function with circuit breaker protection
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Check circuit state
    if (this.state === 'open') {
      const timeSinceFailure = Date.now() - this.lastFailureTime;
      if (timeSinceFailure < this.resetTimeoutMs) {
        throw new Error('Circuit breaker is open - too many failures');
      }
      // Try half-open
      this.setState('half-open');
    }
    
    try {
      const result = await fn();
      
      // Success - reset circuit
      if (this.state === 'half-open') {
        this.setState('closed');
        this.failureCount = 0;
      }
      
      return result;
    } catch (error) {
      this.failureCount++;
      this.lastFailureTime = Date.now();
      
      // Open circuit if threshold exceeded
      if (this.failureCount >= this.failureThreshold) {
        this.setState('open');
      }
      
      throw error;
    }
  }

  private setState(newState: 'closed' | 'open' | 'half-open'): void {
    if (this.state !== newState) {
      this.state = newState;
      console.log(`Circuit breaker state changed to: ${newState}`);
      if (this.onStateChange) {
        this.onStateChange(newState);
      }
    }
  }

  getState(): 'closed' | 'open' | 'half-open' {
    return this.state;
  }

  reset(): void {
    this.failureCount = 0;
    this.lastFailureTime = 0;
    this.setState('closed');
  }
}










